import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import CoverImageInput from '@/components/shared/CoverImageInput';
import { TextInput } from '@/components/ui/daisy';
import { KeyboardKeys } from '@/utils/Keyboard';
import AnswerConfirmEdit from './AnswerConfirmEdit';
import { useMediaPreview } from './useMediaPreview';
import {
  MAX_QUIZ_ANSWERS_FILE_SIZE,
  QUIZ_ANSWERS_ACCEPTED_IMAGE_TYPES,
} from '@/constants';

type AnswerMediaInputProps = {
  initialMediaFile?: File | string | null;
  initialAltText: string;
  onConfirm: (file: File | string, altText?: string) => void;
  onCancel?: () => void;
};

export default function AnswerMediaInput({
  initialMediaFile,
  initialAltText,
  onConfirm,
  onCancel,
}: AnswerMediaInputProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.questionForm',
  });

  const [mediaFile, setMediaFile] = useState<File | string | null>(
    initialMediaFile ?? null,
  );
  const [altText, setAltText] = useState(initialAltText);
  const previewUrl = useMediaPreview(mediaFile);

  const handleImageChange = (url: string) => {
    if (!url) setMediaFile(null);
  };

  const handleFileSelect = (file: File) => {
    setMediaFile(file);
  };

  const handleConfirm = () => {
    if (!mediaFile) {
      onCancel?.();
      return;
    }
    const trimmedAlt = altText.trim();
    const initialAlt = initialAltText.trim();
    const fileChanged = mediaFile !== (initialMediaFile ?? null);
    const altTextChanged = trimmedAlt !== initialAlt;
    if (!fileChanged && !altTextChanged) {
      onCancel?.();
      return;
    }
    onConfirm(mediaFile, trimmedAlt || undefined);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === KeyboardKeys.Enter) {
      e.preventDefault();
      e.stopPropagation();
      handleConfirm();
    }
  };

  return (
    <div className="flex items-center gap-2 justify-between flex-1">
      <div className="flex flex-col gap-2 flex-1">
        <CoverImageInput
          value={previewUrl ?? undefined}
          onChange={handleImageChange}
          onFileSelect={handleFileSelect}
          accept={QUIZ_ANSWERS_ACCEPTED_IMAGE_TYPES}
          limit={MAX_QUIZ_ANSWERS_FILE_SIZE}
        />
        <TextInput
          type="text"
          className="flex-1 text-base-content"
          placeholder={t('altTextPlaceholder')}
          value={altText}
          onChange={e => setAltText(e.target.value)}
          onKeyDown={handleKeyDown}
        />
      </div>

      <AnswerConfirmEdit onConfirm={handleConfirm} />
    </div>
  );
}
